
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from '@vue/composition-api';
import { useContext, useRoute } from '@nuxtjs/composition-api';
import type { Context } from '@nuxt/types';
import { storeToRefs } from 'pinia';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import Auth from '~/helper/Auth';
import { useAuthStore } from '~/store/modal/auth';
import ViewportToogle from '~/components/ViewportToogle/ViewportToogle.vue';
import BPSlider from '~/components/bonus/BPSlider/BPSlider.vue';
import RowSliderNew from '~/components/sliders/RowSliderNew/RowSliderNew.vue';
import BPCardAuthForm from '~/components/bonus/BPCardAuthForm/BPCardAuthForm.vue';
import BPGiftAuthForm from '~/components/bonus/BPGiftAuthForm/BPGiftAuthForm.vue';
import BPQuestion from '~/components/bonus/BPQuestion/BPQuestion.vue';
import BonusCardCondition from '~/components/bonus/BonusCardCondition/BonusCardCondition.vue';
import UserBonusCardBalance from '~/components/profile/UserBonusCardBalance/UserBonusCardBalance.vue';
import { useSocialStore } from '~/store/social';
import { useUserStore } from '~/store/user';
import { useBonusStore } from '~/store/bonus';
import { create, getToken, handle, init } from '~/helper/yandexCaptcha';
type ApiRequestParams = Parameters<typeof context.$api.auth.sendConfirmCode>[0];
const __sfc_main = {};
__sfc_main.setup = (__props, __ctx) => {
  const {
    enable: showAutModal
  } = useAuthStore();
  const socialStore = useSocialStore();
  const appStoreUrl = computed(() => socialStore.social.appStoreUrl);
  const googlePlay = computed(() => socialStore.social.googlePlay);
  const {
    userAuth,
    isUser
  } = storeToRefs(useUserStore());
  const {
    bonusData
  } = storeToRefs(useBonusStore());
  const context = useContext();
  const route = useRoute();
  const link = ref('');
  const mobileType = ref<'android' | 'apple'>('android');
  const currentData = ref<ApiRequestParams>({
    short: true,
    phone: ''
  });
  let getRequest: (params: ApiRequestParams) => void;
  const isYandexCaptchaEnabled = ref(context.$gb?.getFeatureValue('yandex-captcha', false));
  const bonusCountLink = userAuth.value ? '/user/bonus/' : '/bonus-activation/';
  const questions = [{
    question: 'Почему мне не начислили бонусы на день рождения?',
    answer: 'Бонусы на день рождения начисляются при условии, что вы зарегистрировались на сайте 05.ru более чем 90 дней назад, вы не получали данный вид бонусов в последние 365 дней и указали дату рождения в личном кабинете не позднее, чем за сутки до дня рождения.'
  }, {
    question: 'Почему мне не начислили бонусы за реферальную ссылку?',
    answer: 'Бонусы за отправленную ссылку другу начисляются при выполнении следующих условий: <br /> - ваш друг является новым пользователем на сайте 05.ru и зарегистрировался перейдя по ссылке <br /> - ваш друг совершил покупку на 500 и более рублей в интернет-магазине течении 3х месяцев со дня регистрации <br /> При выполнении этих условий, вы получите 350 бонусов, за каждого приглашенного друга.'
  }, {
    question: 'Сколько у меня бонусов? Где посмотреть бонусный баланс?',
    answer: `Бонусный баланс всегда можно проверить в личном кабинете на сайте и в приложении, а также на текущей странице в авторизованном состоянии. <a href="${bonusCountLink}" style="color: black; outline: none; cursor: pointer;">Посмотреть свой баланс</a>`
  }, {
    question: 'Как узнать, за что списались/начислились бонусы?',
    answer: 'В личном кабинете, во вкладке бонусы вы можете найти всю историю ваших бонусов, где указана сумма бонусов, дата и за какое действие это начисление/списание было.'
  }, {
    question: 'Какой срок у бонусов?',
    answer: 'У всех стандартных бонусов (те, что были начислены за покупку товара) срок сгорания составляет 180 календарных дней. При этом, если вы совершите покупку в течении этого периода, срок бонусов будет автоматически продлен на еще 180 дней. <br /> Акционные и промо бонусы имеют индивидуальный срок сгорания, который указывается в условиях акций/промо.'
  }, {
    question: 'За что я могу получить бонусы?',
    answer: 'За покупки - это стандартный кэшбэк, который установлен практически на весь ассортимент товаров; <br /> За покупки акционных товаров - чаще всего это повышенный кэшбэк, который действует на конкретные товары или товарные категории; <br /> За активности на сайте - это бонусы, которые мы начисляем за прохождение тестов и квизов, оставленные отзывы, по праздникам и пр. Эти бонусы не являются постоянным и список активностей может постоянно меняться.'
  }, {
    question: 'Бонусы можно использовать сразу?',
    answer: 'Срок активации бонусов и их использования отличается в зависимости от типа бонусов:<br /> - за покупки - использовать эти бонусы можно через 14 дней со дня покупки, срок сгорания у них 180 календарных дней. Важно помнить, что для товаров с доставкой, отсчет идет со дня получения доставки; <br /> - за покупки акционных товаров - сроки активации и сгорания этих бонусов указываются в условиях к акции; <br /> - за активность на сайте - сроки активации и сгорания этих бонусов также указываются в условиях к активности или в условиях бонусной программы.'
  }, {
    question: 'Что такое “ожидают начисления”?',
    answer: 'В данном блоке отображаются бонусы, которые начислены вам за вашу покупку. Так как бонусы за покупку можно потратить только на 15 день после покупки, сначала они отображаются в этом блоке. После того, как пройдет 14 дней, бонусы будут суммированы с вашим общим балансом, а блок “ожидают начисления” исчезнет.'
  }, {
    question: 'Что делать, если у меня поменялся номер привязанный к виртуальной бонусной карте?',
    answer: 'Если на вашей карте были бонусы - вы можете оставить заявку на смену номера телефона в любом нашем офлайн магазине или через онлайн-менеджера. После того, как заявка пройдет модерацию - ваша карта с бонусами будут привязаны к новому номеру телефона. <br /> Если на вашей карте не было бонусов, или сумма бонусов незначительна - вы можете просто зарегистрировать себе новую карту.'
  }, {
    question: 'Как пользоваться бонусной картой в офлайн магазинах?',
    answer: 'Раньше мы выдавали физические карты - вы можете предъявить ее на кассе. Однако, если вы потеряли свою физическую карту или у вас изначально была виртуальная, воспользуйтесь любым удобным для вас способом: <br />  - назовите свой номер телефона; <br /> - покажите кассиру QR-код в приложении 05.ru.'
  }, {
    question: 'Как получить виртуальную бонусную карту, если у меня уже есть физическая с положительным бонусом?',
    answer: 'Физическая карта по умолчанию является виртуальной картой. То есть, вы можете просто авторизоваться на сайте по номеру, который привязан к вашей физической карте и копить/тратить бонусы как и раньше.'
  }, {
    question: 'У меня есть физическая не активированная карта, как с ней быть? Как ее активировать?',
    answer: 'Для активации физической карты в офлайн магазинах - просто подойдите на кассу и сообщите о проблеме, кассир поможет вам с активацией. <br /> Для активации карты на сайте перейдите на <a href="/bonus-activation" style="color: black; outline: none; cursor: pointer;">страницу активации</a>.'
  }, {
    question: 'Как перевести бонусы с неактивированной физической карты, на только что созданную виртуальную?',
    answer: 'Если на вашей физической карте были бонусы - просто <a href="/bonus-activation" style="color: black; outline: none; cursor: pointer;">активируйте карту</a>. и виртуальная карта отвяжется от вашего личного кабинета, а физическая с положительным балансом будет к нему привязана. <br /> Если на физической карте бонусов не было - просто пользуйтесь новой виртуальной картой.'
  }, {
    question: 'Зависит ли количество бонусов от суммы покупки?',
    answer: 'Количество начисляемых бонусов разнится в зависимости от категории товара, модели, от того, является ли товар акционным и прочее. Сумма начисляемых бонусов за товар всегда указывается на сайте рядом с ценой и на ценнике в офлайн магазинах.'
  }, {
    question: 'Я не нашел ответ на свой вопрос',
    answer: 'Если у вас все еще остались вопросы или нерешенные проблемы, свяжитесь с нами через онлайн оператора, мы обязательно свяжемся с вами, чтобы решить ваш вопрос.'
  }];
  const isEnd = ref(false);
  const observerEl = ref(null);
  const observer = ref<IntersectionObserver | null>(null);
  const viewType = computed(() => (route.value.query.faq as string));
  onMounted(() => {
    if (isYandexCaptchaEnabled.value) init();
    window.onCreateCaptcha = () => {
      create({
        onSuccess(token) {
          getRequest({
            phone: currentData.value?.phone,
            short: currentData.value?.short,
            recaptchaToken: token
          });
        }
      }, context);
    };
    if (observerEl.value) {
      observer.value = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            isEnd.value = true;
            observer.value?.disconnect();
          }
        });
      }, {
        threshold: 1
      });
      observer.value?.observe(observerEl.value);
    }
    if (/Android/i.test(navigator.userAgent)) {
      link.value = googlePlay.value;
      mobileType.value = 'android';
    }
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
      link.value = appStoreUrl.value;
      mobileType.value = 'apple';
    }
    if (viewType.value) {
      nextTick(() => {
        /**
         * Если у query параметр faq = referal || review, то проваливаемся до конкретного пунтка
         * Иначе до общего блока "Условия бонусной программы"
         */
        scrollIntoView(document.querySelector(viewType.value === 'show' ? '#faq' : '.sub-rule_mark')!, {
          block: 'center',
          behavior: 'smooth'
        });
      });
    }
  });
  onBeforeUnmount(() => {
    if (observer.value && observerEl.value) observer.value.unobserve(observerEl.value);
  });
  function onSuccess(this: Context, data: any) {
    Auth({
      contextData: this,
      response: data,
      request: this.$api.user.sync
    });
  }
  function handleCaptchaSubmit(request: (params: ApiRequestParams) => Promise<void>, data: ApiRequestParams) {
    currentData.value = data;
    getRequest = request;
    if (context.$config.deployEnvironment === 'production' && isYandexCaptchaEnabled.value) {
      const token = getToken();
      handle({
        token,
        callback: (token: string) => request({
          phone: currentData.value.phone,
          short: currentData.value.short,
          recaptchaToken: token
        })
      }, context);
    } else {
      request({
        phone: currentData.value.phone,
        short: currentData.value.short
      });
    }
  }
  return {
    showAutModal,
    appStoreUrl,
    googlePlay,
    userAuth,
    isUser,
    bonusData,
    link,
    mobileType,
    questions,
    isEnd,
    observerEl,
    viewType,
    onSuccess,
    handleCaptchaSubmit
  };
};
__sfc_main.components = Object.assign({
  UserBonusCardBalance,
  BPCardAuthForm,
  ViewportToogle,
  BPSlider,
  BPQuestion,
  RowSliderNew,
  BPGiftAuthForm,
  BonusCardCondition
}, __sfc_main.components);
export default __sfc_main;
